import { ActionCardModel, QuestionNode, GenericAiWidgetNode } from '../actionCard'
import getHtmlFromMarkdown from '@playbooks/core/src/utils/getHtmlFromMarkdown'

const dangerousTags = [
  '<applet',
  '<style',
  '<link',
  '<iframe',
  '<script',
  '<img',
  '<video',
  '<comment',
  '<embed',
  '<link',
  '<listing',
  '<meta',
  '<noscript',
  '<object',
  '<plaintext',
  '<script',
  '<xmp',
  'onmouseover=',
  'onerror=',
]

// Sanitizes an HTML string by removing potentially dangerous tags and attributes.
export function sanitize(html: string): string {
  let sanitizedHtml = html
  dangerousTags.forEach(tag => {
    // Create a regular expression to match the dangerous tag, case-insensitive
    const regex = new RegExp(tag, 'gi')
    // Replace all occurrences of the dangerous tag with an empty string
    sanitizedHtml = sanitizedHtml.replace(regex, '')
  })
  return sanitizedHtml
}

export class ReplacementProcessor {
  private __actionCardModel: ActionCardModel
  private __replacementsMap?: { key: string; value: string }[]

  // Array of element types to skip during the replacement process
  private __elementsTypesToSkip: string[] = []

  // Flag indicating whether Markdown content should be preserved for GenericAiWidgetNode replacements
  private __markdownSaved: boolean = false

  private get __ReplacementsMap() {
    if (!this.__replacementsMap) {
      this.__replacementsMap = this.__getReplacementsMap()
    }

    return this.__replacementsMap
  }

  constructor(actionCardModel: ActionCardModel) {
    this.__actionCardModel = actionCardModel
  }

  /**
   * Listed element kinds will be skipped during the replacement process.
   * @param elementsTypesToSkip Array of element types to skip during the replacement process.
   * @returns The current instance for chaining.
   */
  public skipElementTypes(elementsTypesToSkip: string[] = []): this {
    this.__replacementsMap = undefined
    this.__elementsTypesToSkip = elementsTypesToSkip
    return this
  }

  /**
   * Enable saving Markdown content (GenericAiWidgetNode replacements return HTML by default)
   * @returns The current instance for chaining.
   */
  public saveMarkdown() {
    this.__replacementsMap = undefined
    this.__markdownSaved = true
    return this
  }

  public processReplacements(text: string): string {
    if (!text || !text.includes('$')) {
      return text
    }

    this.__ReplacementsMap.forEach(({ key, value }: { key: string; value: string }) => {
      text = text.replaceAll(key, value)
    })
    return text
  }

  private __getReplacementsMap(): { key: string; value: string }[] {
    const map: { key: string; value: string }[] = []

    const searchService = this.__actionCardModel.getSearchService()

    // add to the map QuestionNode values
    searchService.filterQuestions().forEach<QuestionNode>(questionNode => {
      const type = questionNode.getType()

      if (type && this.__elementsTypesToSkip.includes(type)) {
        return
      }

      const code = questionNode.getCode()
      if (code) {
        map.push({ key: '$'.concat(code), value: this.__getReplacementValue(questionNode) })
      }
    })

    // add to the map GenericAiWidgetNode values
    searchService
      .filter(x => x instanceof GenericAiWidgetNode)
      .forEach<GenericAiWidgetNode>(widgetNode => {
        const type = widgetNode.getType()

        if (type && this.__elementsTypesToSkip.includes(type)) {
          return
        }

        const code = widgetNode.getCode()
        if (code) {
          // If this.__markdownSaved is true, use the raw Markdown value; otherwise, convert it to HTML
          const value = this.__markdownSaved
            ? widgetNode.getValue() || widgetNode.buttonLabel
            : getHtmlFromMarkdown(widgetNode.getValue() || widgetNode.buttonLabel)
          map.push({ key: '$'.concat(code), value: sanitize(value) })
        }
      })

    return map
  }

  private __getReplacementValue(questionNode: QuestionNode): string {
    const replacementValue =
      questionNode.stringifyValue() || `(${questionNode.getLabelTemplate()})` || questionNode.getType() || ''
    return sanitize(replacementValue)
  }
}
